import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { etiquetaDeLocales } from '@alexiarest/shared';
import { CONSOLIDATED_ROUTES, LOCATION_ROUTES, useLocationScope } from '../context/LocationContext';

export { LOCATION_ROUTES };

/**
 * «Todos los locales (3) ▾» junto al mes (Alex, 12 oct 2026; bloque C, 14
 * oct 2026). Solo sale si hay más de un local. En las pantallas que suman
 * (Panel, Ventas, Estado de resultados, Análisis…) se marcan los que se
 * quieran —Centro + Norte también— y al lado de cada uno «Solo este». La
 * API ya acepta varios (`?locationIds=a,b`). Lo marcado se queda al pasar
 * de una pantalla a otra. Donde se edita local a local (Gastos,
 * Inversiones, Impuestos) se elige uno.
 */
export function LocationPicker(): JSX.Element | null {
  const { locations, locationId, seleccion, setSeleccion } = useLocationScope();
  const { pathname } = useLocation();
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const menuId = useId();
  useCerrarFuera(caja, abierto, () => setAbierto(false));

  if (!LOCATION_ROUTES.has(pathname) || locations.length <= 1) return null;
  const varios = CONSOLIDATED_ROUTES.has(pathname);
  const ids = locations.map((l) => l.locationId);
  const validos = seleccion.filter((id) => ids.includes(id));
  const marcados = varios ? (validos.length ? validos : ids) : [locationId ?? ids[0]];
  const todos = varios && marcados.length === ids.length;
  const etiqueta = varios ? etiquetaDeLocales(locations, todos ? undefined : marcados) : locations.find((l) => l.locationId === marcados[0])?.name ?? 'Local';

  const alternar = (id: string) => {
    if (!varios) {
      setSeleccion([id]);
      setAbierto(false);
      return;
    }
    const quedan = marcados.includes(id) ? marcados.filter((m) => m !== id) : [...marcados, id];
    // Sin ninguno no se puede mirar nada: se queda el que había.
    if (!quedan.length) return;
    setSeleccion(quedan.length === ids.length ? [] : quedan);
  };

  return (
    <div className="filtro-cabecera" ref={caja} data-tour="local">
      <button
        type="button"
        className="filtro-cabecera__boton"
        aria-haspopup="true"
        aria-expanded={abierto}
        aria-controls={menuId}
        aria-label={`Local: ${etiqueta}`}
        onClick={() => setAbierto((v) => !v)}
      >
        {etiqueta} <span aria-hidden="true">▾</span>
      </button>
      {abierto && (
        <div className="filtro-cabecera__menu" id={menuId} role="group" aria-label="Locales">
          {varios && (
            <label className="filtro-cabecera__opcion">
              <input type="checkbox" checked={todos} onChange={() => setSeleccion([])} />
              Todos los locales
            </label>
          )}
          {locations.map((l) => (
            <div key={l.locationId} className="filtro-cabecera__fila">
              <label className="filtro-cabecera__opcion">
                <input
                  type={varios ? 'checkbox' : 'radio'}
                  name={varios ? undefined : `${menuId}-local`}
                  checked={marcados.includes(l.locationId)}
                  onChange={() => alternar(l.locationId)}
                />
                {l.name}
              </label>
              {varios && (
                <button type="button" className="filtro-cabecera__solo" onClick={() => { setSeleccion([l.locationId]); setAbierto(false); }}>
                  Solo este
                </button>
              )}
            </div>
          ))}
          <p className="filtro-cabecera__nota">
            {varios ? 'Lo que elijas aquí se queda igual en el resto de Finanzas y en el Panel.' : 'Aquí se ve y se anota local a local.'}
          </p>
        </div>
      )}
    </div>
  );
}

/** Cierra un menú desplegable al tocar fuera o con Escape. */
export function useCerrarFuera(ref: React.RefObject<HTMLElement>, abierto: boolean, cerrar: () => void): void {
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) cerrar();
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar();
    };
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('pointerdown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto, cerrar, ref]);
}
